import { useEffect, useRef } from 'react';
import { StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native';

import { tokens } from '../../tokens';
import { Badge } from '../badge';
import { Pressable, PressableProps, usePressableState } from '../pressable';
import { Text } from '../text';

const SCROLL_BUFFER = 48;

interface Props extends PressableProps {
  isActive: boolean;
  onPress: PressableProps['onPress'];
  title: string;
  titleStyle?: StyleProp<TextStyle>;
  scrollIntoFirstPositionInView?: (opts: { position: number; offset?: number }) => void;
  badge?: number;
  style?: StyleProp<ViewStyle>;
}

export const Anchor = (props: Props) => {
  const ref = useRef<View>(null);
  const layoutRef = useRef({ x: 0, y: 0, height: 0, width: 0 });

  const {
    isActive,
    title,
    badge,
    scrollIntoFirstPositionInView,
    onPress,
    style,
    titleStyle,
    ...pressableProps
  } = props;

  useEffect(() => {
    if (!isActive) {
      ref.current?.blur?.();
      return;
    }

    scrollIntoFirstPositionInView &&
      scrollIntoFirstPositionInView({
        position: layoutRef.current.x,
        offset: SCROLL_BUFFER,
      });
  }, [isActive, scrollIntoFirstPositionInView]);

  return (
    <Pressable
      ref={ref}
      onLayout={event => {
        layoutRef.current = event.nativeEvent.layout;
      }}
      onPress={onPress}
      borderRadius={0}
      style={[styles.pressable, isActive && styles.isActivePressable, style]}
      focusedStyle={styles.isFocusedPressable}
      hoveredStyle={styles.isHoveredPressable}
      outlineStyle={styles.hideOutline}
      outlinePressedStyle={styles.hideOutline}
      outlineFocusedStyle={styles.hideOutline}
      outlineDisabledStyle={styles.hideOutline}
      outlineHoveredStyle={styles.hideOutline}
      {...pressableProps}
    >
      <Title isActive={isActive} title={title} style={titleStyle} />
      {badge && (
        <Badge
          circular
          circularSize={20}
          style={{ marginLeft: 4 }}
          color={tokens.colors.$houseRedDarken}
        >
          {badge}
        </Badge>
      )}
    </Pressable>
  );
};

const Title = ({
  isActive,
  title,
  style,
}: {
  isActive: boolean;
  title: string;
  style?: StyleProp<TextStyle>;
}) => {
  const pressableState = usePressableState();
  return (
    <Text.Ui
      size="sm"
      weight="semibold"
      style={[
        isActive && styles.isActiveText,
        pressableState.hovered && styles.isHoveredText,
        style,
      ]}
    >
      {title}
    </Text.Ui>
  );
};

const styles = StyleSheet.create({
  pressable: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 0,
    borderBottomWidth: 3,
    borderColor: tokens.colors.$blackOpacity10,
    flexDirection: 'row',
  },
  isActivePressable: {
    borderColor: tokens.colors.$houseRedDarken,
  },
  isHoveredPressable: {
    borderColor: tokens.colors.$houseRedDarken,
  },
  isFocusedPressable: {
    borderColor: tokens.colors.$houseRedDarken,
  },
  isActiveText: {
    color: tokens.colors.$houseRedDarken,
  },
  isHoveredText: {
    color: tokens.colors.$houseRedDarken,
  },
  isFocusedText: {
    color: tokens.colors.$houseRedDarken,
  },
  hideOutline: {
    borderColor: tokens.colors.$transparent,
  },
});
